export function pickHintCandidate(
  removableIds: readonly string[],
  isHinted: (arrowId: string) => boolean,
  isBusy: (arrowId: string) => boolean,
): string | null {
  return removableIds.find((arrowId) => !isHinted(arrowId) && !isBusy(arrowId)) ?? null;
}

export class LevelSession {
  private readonly failureLimit: number;
  private failures = 0;
  private totalFailures = 0;
  private continuations = 0;
  private hintsUsed = 0;
  private reachedGameOver = false;
  private readonly failedArrowIds = new Set<string>();
  private readonly hintedArrowIds = new Set<string>();

  constructor(maxFailures = 3) {
    this.failureLimit = maxFailures;
  }

  get failCount(): number {
    return this.failures;
  }

  get attemptFailCount(): number {
    return this.totalFailures;
  }

  get continuedCount(): number {
    return this.continuations;
  }

  get hintUsedCount(): number {
    return this.hintsUsed;
  }

  get maxFailures(): number {
    return this.failureLimit;
  }

  get isGameOver(): boolean {
    return this.failCount >= this.maxFailures;
  }

  get hasReachedGameOver(): boolean {
    return this.reachedGameOver;
  }

  isFailed(arrowId: string): boolean {
    return this.failedArrowIds.has(arrowId);
  }

  isHinted(arrowId: string): boolean {
    return this.hintedArrowIds.has(arrowId);
  }

  recordBlocked(arrowId: string): boolean {
    if (this.isGameOver) {
      return false;
    }

    this.failedArrowIds.add(arrowId);
    this.failures += 1;
    this.totalFailures += 1;
    if (this.isGameOver) {
      this.reachedGameOver = true;
    }
    return this.isGameOver;
  }

  clearResolvedFailures(canRemove: (arrowId: string) => boolean): string[] {
    const resolved: string[] = [];
    for (const arrowId of this.failedArrowIds) {
      if (canRemove(arrowId)) {
        this.failedArrowIds.delete(arrowId);
        resolved.push(arrowId);
      }
    }
    return resolved;
  }

  markHinted(arrowId: string): void {
    this.hintedArrowIds.add(arrowId);
    this.hintsUsed += 1;
  }

  recordRemoved(arrowId: string): void {
    this.failedArrowIds.delete(arrowId);
    this.hintedArrowIds.delete(arrowId);
  }

  continueRun(): void {
    this.failures = 0;
    this.continuations += 1;
  }
}
